<script lang="ts">
import { Dropdown } from 'bootstrap'
import { useTheme } from 'frontend/themes/composables/useTheme'
import { throttle } from 'lodash'
import { isVNode, nextTick, ref } from 'vue'

interface IDropdownActions {
  type?: 'header' | 'divider' | 'a'
  title?: string
  subtitle?: string
  href?: string
  target?: string
  classes?: string[]
  disabled?: boolean
  clickFn?: () => void
}

export default {
  name: 'Dropdown',
  emits: ['open', 'hidden'],
  props: {
    actions: { type: Array, default: () => [] as IDropdownActions[] },
    stopTogglerPropagation: { type: Boolean, default: false },
    alignRight: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    autoClose: { type: [String, Boolean], default: true },
    strategy: { type: String, default: 'absolute' },
    disabled: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: false },
    preventMouseUp: { type: Boolean, default: true },
  },
  setup() {
    const dropdownTriggerRef = ref(null)
    const dropdownMenuRef = ref(null)
    const isOpened = ref(false)
    const maxHeight = ref(Math.round(window.innerHeight * 0.8))
    const { currentThemeType } = useTheme()

    function recomputeMaxHeight() {
      const dropdownTop =
        (dropdownMenuRef.value?.getBoundingClientRect()?.top || 0) + window.scrollY
      let result = Math.round(window.innerHeight - dropdownTop - 55)
      if (result < 100) {
        result = Math.round(window.innerHeight * 0.3)
      }
      maxHeight.value = result
    }

    const throttledRecomputeMaxHeight = throttle(recomputeMaxHeight, 100)

    return {
      throttledRecomputeMaxHeight,
      dropdownTriggerRef,
      dropdownMenuRef,
      isOpened,
      maxHeight,
      recomputeMaxHeight,
      isVNode,
      currentThemeType,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.isOpened
      },
      set(newVal) {
        if (newVal && !this.isOpened) {
          this.show()
        } else if (!newVal && this.isOpened) {
          this.closeFn()
        }
      },
    },
    hoverListeners() {
      return this.hoverable
        ? {
            onMouseenter: this.show,
            onMouseleave: this.closeFn,
          }
        : {}
    },
    actionsPresent() {
      return this.actions.length > 0
    },
  },
  watch: {
    dropdownTriggerRef: {
      handler(newEl, oldEl) {
        if (newEl && !oldEl) {
          new Dropdown(newEl, {
            offset: this.hoverable ? [0, -1] : [],
            autoClose: false,
            popperConfig: {
              strategy: this.strategy,
              boundary: 'viewport',
            },
          })
          newEl.addEventListener('show.bs.dropdown', () => {
            this.$nextTick(() => {
              this.recomputeMaxHeight()
              this.isOpened = true
              this.$emit('open', true)
            })
          })
          newEl.addEventListener('hidden.bs.dropdown', () => {
            this.isOpened = false
            this.$emit('hidden', false)
          })
        }
      },
      immediate: true,
    },
    isOpened(value) {
      if (value) {
        this.$eventHub.$on('mouseup', this.closeIfAutoClose)
        this.$eventHub.$on('windowResize', this.repositionAndRecomputeHeight)
        this.repositionAndRecomputeHeight()
      } else {
        this.$eventHub.$off('mouseup', this.closeIfAutoClose)
        this.$eventHub.$off('windowResize', this.repositionAndRecomputeHeight)
        this.throttledRecomputeHeight?.cancel()
      }
    },
    actions() {
      nextTick(() => {
        this.reposition()
      })
    },
  },
  unmounted() {
    this.$eventHub.$off('mouseup', this.closeIfAutoClose)
  },
  methods: {
    show() {
      if (this.dropdownTriggerRef) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.show()
      }
    },
    reposition() {
      if (this.alignRight && this.dropdownTriggerRef && this.isOpened) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.update()
      }
    },
    repositionAndRecomputeHeight() {
      this.reposition()
      this.throttledRecomputeMaxHeight()
    },
    closeIfAutoClose(event) {
      if (
        this.autoClose &&
        !this.dropdownTriggerRef?.contains(event.target) &&
        this.dropdownTriggerRef != event.target &&
        !(event.target.closest('.popoverContent') || event.target.closest('.flatpickr-calendar'))
      ) {
        this.closeFn()
      }
    },
    closeFn() {
      if (this.dropdownTriggerRef) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.hide()
      }
    },
    close() {
      this.closeFn()
    },
    open() {
      if (this.dropdownTriggerRef) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.show()
      }
    },
    togglerClicked(event) {
      if (this.stopTogglerPropagation) event.stopPropagation()
    },
    preventIfShould(event) {
      if (this.preventMouseUp) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
  },
  expose: ['close', 'open', 'isOpen'],
}
</script>

<template lang="pug">
.dropdown.theme-wrapper(
  v-if="actionsPresent || !!$slots['items']"
  v-bind="hoverListeners"
  :class="{ disabled }"
  :data-theme="currentThemeType"
)
  div(
    ref="dropdownTriggerRef"
    :data-bs-auto-close="autoClose"
    @click="togglerClicked($event)"
    data-bs-toggle="dropdown"
  )
    slot(:closeFn="closeFn" :isOpen="isOpen" :isOpened="isOpened" :show="show")
      button.btn.btn-sm.btn-default.dropdown-toggle
        i.bi-three-dots

  ul.dropdown-menu(
    ref="dropdownMenuRef"
    :class="{ 'dropdown-menu-end': alignRight, scrollable }"
    @mouseup="preventIfShould($event)"
  )
    slot(
      :close="close"
      :closeFn="closeFn"
      :isOpen="isOpen"
      :isOpened="isOpened"
      :maxHeight="maxHeight"
      name="items"
    )
      template(
        v-for="(action, actionIndex) in actions"
        :key="action.type === 'custom' ? 'custom-' + actionIndex : action.title"
      )
        li(v-if="action.type === 'custom'" @click.stop)
          slot(:action="action" :closeFn="closeFn" name="customContent")
        li(
          v-else-if="!!action.href && (!action.type || action.type == 'a')"
          :class="{ disabled: !!action.disabled }"
          v-tooltip.options="{ title: action?.tooltip, placement: 'left' }"
        )
          a.dropdown-item(
            v-if="!action.type || action.type == 'a'"
            :class="{ disabled: !!action.disabled }"
            :href="action.href"
            :target="action.target || '_self'"
          )
            component(v-if="isVNode(action.title)" :is="action.title")
            span(v-else) {{ action.title }}
        li(
          v-else
          :class="{ disabled: !!action.disabled }"
          @click.prevent.stop="action.clickFn && action.clickFn($event, closeFn)"
          v-tooltip.options="{ title: action?.tooltip, placement: 'left' }"
        )
          a.dropdown-item(
            v-if="!action.type || action.type == 'a'"
            :class="[{ disabled: !!action.disabled }, action.classes]"
          )
            i.dropdown-item-icon.me-1(v-if="action.iconClasses" :class="action.iconClasses")
            component(v-if="isVNode(action.title)" :is="action.title")
            span(v-else) {{ action.title }}
          hr.dropdown-divider(v-if="action.type === 'divider'")
          h6.dropdown-header(v-if="action.type === 'header'" :class="action.classes")
            component(v-if="isVNode(action.title)" :is="action.title")
            span(v-else) {{ action.title }}
            .small.lh-1.text-black-30.ms-2.text-pre-wrap(v-if="action.subtitle?.length") {{ action.subtitle }}
</template>

<style lang="scss" scoped>
@import 'frontend_stylesheets/_variables.scss';
.dropdown {
  .dropdown-toggle {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 100%;
    cursor: pointer;

    &:after {
      display: none;
    }

    &:hover {
      background: $gray;
    }

    .dots-icon {
      width: 1rem;
    }

    .bi-three-dots {
      font-size: 1.25rem;
    }
  }
}
</style>
<style lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;
@import 'frontend_stylesheets/_variables.scss';

.dropdown {
  .dropdown-toggle {
    cursor: pointer;

    &::after {
      content: '';
      border: none;
      font-family: 'Material Icons Outlined';
      font-weight: normal;
      font-style: normal;
      font-size: 1.2em;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      overflow-wrap: normal;
      direction: ltr;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizelegibility;
      font-feature-settings: 'liga';
      margin: 0;
    }
  }
  .dropdown-menu {
    border-radius: 8px;
    box-shadow: $box-shadow-dropdown;
    background: map.get(themeVariables.$colors, containerLowest);
    border: 1px solid map.get(themeVariables.$colors, outlineVariant);

    color: map.get(themeVariables.$colors, onSurface);
    &.dropdown-menu-end {
      border-top-right-radius: 0;
    }
    &:not(.dropdown-menu-end):not(.footer-top) {
      border-top-left-radius: 0;
    }
    &.scrollable {
      max-height: 80vh;
      overflow: auto;
      @include for-size(small-and-medium-devices) {
        max-height: 60vh;
      }
    }
    .dropdown-item {
      cursor: pointer;
      font-size: 0.85rem;
      color: map.get(themeVariables.$colors, onSurface);

      &:hover {
        background: map.get(themeVariables.$colors, containerHighest);
        color: map.get(themeVariables.$colors, onSurface);
      }

      &.color-success {
        color: map.get(themeVariables.$colors, success);

        &:hover {
          background: map.get(themeVariables.$colors, success);
          color: map.get(themeVariables.$colors, containerLowest);
        }
      }

      &.color-error {
        color: map.get(themeVariables.$colors, error);

        &:hover {
          background: map.get(themeVariables.$colors, error);
          color: map.get(themeVariables.$colors, onError);
        }
      }
    }
    li > * {
      width: 100%;
    }
  }
}

.dropdown-item-icon {
}
</style>
